export type PatternInput = {
  tag_id?: number | null;
  name_ko?: string | null;
  name_en?: string | null;
  name_ja?: string | null;
};

export type ClassificationFormValue = {
  audienceCategoryIds: number[];
  primaryColorOptionKey?: string | null;
  patterns: PatternInput[];
};

export type ColorOptionLike = {
  option_key?: string | null;
  color_id?: number | string | null;
  id?: number | string | null;
  color_name?: string | null;
  hex_code?: string | null;
};

export const normalizeHex = (raw: string) => {
  const value = raw.trim().toUpperCase();
  const withHash = value.startsWith("#") ? value : `#${value}`;
  if (!/^#[0-9A-F]{6}$/.test(withHash)) {
    throw new Error("HEX 색상은 #RRGGBB 형식으로 입력해주세요.");
  }
  return withHash;
};

export const normalizedColorKey = (name: string, hex: string) =>
  `${name.trim().toLocaleLowerCase()}:${normalizeHex(hex)}`;

export const getAudienceLabel = (audience: { name_ko?: string | null; name_en?: string | null }) =>
  [audience.name_ko, audience.name_en].filter(Boolean).join(" / ");

export const getColorOptionIdentity = (option: ColorOptionLike) =>
  String(
    option.option_key ??
      option.color_id ??
      option.id ??
      `${option.color_name || ""}:${option.hex_code || ""}`,
  );

export const findColorOptionIndex = (options: ColorOptionLike[], identity: string) =>
  options.findIndex((option) => getColorOptionIdentity(option) === String(identity));

export const createOptionKey = () => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (character) => {
    const random = Math.floor(Math.random() * 16);
    const value = character === "x" ? random : (random & 0x3) | 0x8;
    return value.toString(16);
  });
};

export const buildClassificationPayload = (value: ClassificationFormValue) => {
  if (new Set(value.audienceCategoryIds).size !== value.audienceCategoryIds.length) {
    throw new Error("앱 대분류를 중복 선택할 수 없습니다.");
  }
  const tagIds = value.patterns.map((pattern) => pattern.tag_id).filter(Boolean);
  if (new Set(tagIds).size !== tagIds.length) {
    throw new Error("패턴을 중복 선택할 수 없습니다.");
  }
  return {
    audience_category_ids: value.audienceCategoryIds,
    primary_color_option_key: value.primaryColorOptionKey || null,
    patterns: value.patterns,
  };
};

export type Rgb = { r: number; g: number; b: number };

const clampChannel = (value: number) => Math.min(255, Math.max(0, Math.round(Number(value) || 0)));

export const isValidHex = (raw: string) => /^#?[0-9A-Fa-f]{6}$/.test(String(raw ?? "").trim());

export const hexToRgb = (raw: string): Rgb => {
  const value = normalizeHex(raw).slice(1);
  return {
    r: parseInt(value.slice(0, 2), 16),
    g: parseInt(value.slice(2, 4), 16),
    b: parseInt(value.slice(4, 6), 16),
  };
};

export const rgbToHex = ({ r, g, b }: Rgb) =>
  `#${[r, g, b].map((channel) => clampChannel(channel).toString(16).padStart(2, "0")).join("")}`.toUpperCase();

// 스와치 위에 올릴 글자색. 밝은 배경이면 검정, 어두우면 흰색.
export const readableTextColor = (hex: string) => {
  const { r, g, b } = hexToRgb(hex);
  return (r * 299 + g * 587 + b * 114) / 1000 > 150 ? "#111111" : "#FFFFFF";
};
